<script setup>
// Astra DS · Transport — la barra de transporte de la emisión.
//
// Figma: Pattern/Transport 635:369.
//
// «Onda = masa · progreso = dato · tiempo y control en voz baja. El control es
// una CELDA del módulo, no un botón suelto.» Es la descripción del nodo, y es
// la regla que resolvió el botón de play que flotaba junto a la onda: no había
// que hacerlo más pequeño, había que meterlo dentro del módulo.
//
// La onda es presentacional: recibe `levels` y los pinta. Quien los saca del
// audio real es `useAudioLevels`, para que este componente se pueda montar en
// el banco de pruebas sin que suene nada.
import { computed, ref, watch } from "vue";
import ButtonDs from "./ButtonDs.vue";
import { crearDinamica } from "@/utils/dinamicaDeBanda";

const props = defineProps({
  // Amplitudes 0..1, una por barra. Vacío = la onda se queda en reposo, con
  // las alturas del artefacto.
  levels: { type: Array, default: () => [] },

  playing: { type: Boolean, default: false },

  // Segundos. Con duration a 0 la emisión no tiene final conocido y el tiempo
  // se lee como reloj de directo, no como progreso.
  elapsed: { type: Number, default: 0 },
  duration: { type: Number, default: 0 },

  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },

  // 🔴 Si en este navegador se puede leer el audio: `esperando`, `si` o `no`.
  //
  // Lo decide `useAudioLevels` midiendo, no mirando el user agent. Con `no`, el
  // medidor de 56 barras **no se pinta**: en Safari y en el móvil el analizador
  // nunca entrega muestras, y unas barras quietas donde debería haber sonido se
  // leen como avería. Queda el raíl de progreso y el reloj, que es lo que de
  // verdad demuestra que la emisión corre.
  //
  // `esperando` pinta el medidor en su forma de reposo, porque son los diez
  // segundos del búfer de HLS y todavía no se sabe.
  analisis: { type: String, default: "esperando" },

  /**
   * Si se puede pinchar la barra para ir a un punto.
   *
   * 🔴 **Falso por defecto, y no por prudencia: por sentido.** El transporte
   * nació para la emisión en directo, donde no hay a dónde ir — la radio no se
   * adelanta—. Se enciende donde el audio es una pista con principio y final,
   * como el tema de un trabajo en Radar.
   */
  buscable: { type: Boolean, default: false },

  /**
   * El transporte se apoya en una IMAGEN y no en lienzo liso.
   *
   * Entrega `docs/entregas/2026-09-18-el-velo-del-hero-pasa-a-meseta.md` § 3.
   * 🔴 **Lo pide la superficie, no la pantalla**: hoy son la portada, sobre la
   * meseta de tinta, y Radio, sobre la placa de señal. Donde detrás hay lienzo
   * —el catálogo del sistema, las fichas sin imagen— no cambia nada.
   *
   * Lleva el módulo a tinta: fondo `bg/ink` al 25 %, contorno `border/ink`,
   * reloj en `text/on-ink`, ocho de aire antes del control y el control macizo
   * en `bg/accent`, cuadrado del alto de la barra.
   *
   * El reparto es una regla y no un número: reloj 123 y control del alto, con
   * ocho de aire entre los dos, y la onda se queda con lo que sobre. Así el
   * mismo componente sirve a los 478 de la portada y a los 540 de Radio.
   */
  sobreImagen: { type: Boolean, default: false },
});

const emit = defineEmits(["toggle", "buscar"]);

// Las 56 alturas del nodo, en px sobre un alto de 28. No es una curva
// generada: son los valores del artefacto, y la onda en reposo tiene que ser
// reconocible como la misma que dibujó el diseño.
const BASE = [
  8, 23, 18, 14, 9, 24, 19, 14, 10, 24, 20, 15, 10, 25, 20, 15, 11, 25, 21, 16,
  11, 26, 21, 17, 12, 27, 22, 17, 12, 27, 22, 18, 13, 28, 23, 18, 13, 9, 24, 19,
  14, 9, 24, 19, 15, 10, 25, 20, 15, 10, 25, 20, 16, 11, 26, 21,
];

// ¿Hay señal de verdad, o solo ceros? Importa: la emisión llega por HLS y tras
// pulsar play el analizador devuelve ceros unos diez segundos, hasta que el
// búfer entrega audio decodificado. Si en esa espera se aplanaran las barras,
// la onda parecería averiada justo en el momento en que el usuario acaba de
// pedirla. En reposo se queda quieta con su forma, y arranca cuando llega.
const hasSignal = computed(() =>
  props.levels.some((v) => typeof v === "number" && v > 0.01),
);

// Tres estados, no dos, y la diferencia importa:
//
//   sonando con señal   las barras siguen al audio
//   sonando sin señal   forma de reposo — es el búfer de HLS, no una avería
//   parado              barras bajas y apagadas: no hay nada que medir
//
// El tercero faltaba: en reposo se quedaban a plena altura y eso se lee como
// un vúmetro activo con la música justo en su sitio. Un medidor sin señal no
// marca alto: marca poco.
// 🔴 Cada banda se mide contra sí misma. Pintando la amplitud tal cual, la onda
// se movía un 5% y siempre con la misma rampa: medido con la emisión sonando
// (28.08.26), los graves iban de 0,82 a 1 y los agudos de 0,32 a 0,78, cada
// zona encerrada en su franja. Lo que se veía era el reparto de energía del
// espectro, que apenas cambia, y no lo que hace la música. El porqué entero
// está en `dinamicaDeBanda`, que es el mismo motor de `MiniWave`.
//
// El suelo lo pone la propia dinámica: una barra que cae a cero deja huecos en
// la masa y la onda se lee como rota, no como silencio.
const dinamica = crearDinamica(BASE.length, { minimo: 0.25 });
const dinamicas = ref([]);

watch(
  () => props.levels,
  (l) => {
    // Solo se alimenta cuando hay algo que medir: con la onda parada o en el
    // búfer de HLS, seguir empujando ceros arrastraría las ventanas de cada
    // banda y el primer golpe de verdad llegaría descolocado.
    if (props.playing && hasSignal.value) dinamicas.value = dinamica.normaliza(l);
  },
  { immediate: true },
);

// La silueta del artefacto se conserva: el nivel MODULA las alturas del nodo,
// no las sustituye. Por eso la onda en reposo sigue siendo reconocible como la
// que dibujó el diseño.
const bars = computed(() =>
  BASE.map((h, i) => {
    if (!props.playing) return { h, scale: 0.16 };
    const escala = dinamicas.value[i];
    const scale =
      hasSignal.value && typeof escala === "number" ? escala : 1;
    return { h, scale };
  }),
);

const progress = computed(() => {
  if (!props.duration) return 0;
  return Math.min(Math.max(props.elapsed / props.duration, 0), 1) * 100;
});

// ── Ir a un punto ──
//
// El componente **no toca el audio**: dice a qué segundo quieren ir y quien lo
// monta decide. Es lo mismo que hace con `toggle`, y por lo mismo: aquí no vive
// ninguna fuente de sonido.
//
// `pointerdown` y no `click`: se busca arrastrando desde el primer contacto, y
// esperar a soltar da la sensación de que la barra no responde.
function alPinchar(evento) {
  if (!props.buscable || !props.duration) return;
  const caja = evento.currentTarget.getBoundingClientRect();
  if (!caja.width) return;
  const parte = Math.min(Math.max((evento.clientX - caja.left) / caja.width, 0), 1);
  emit("buscar", parte * props.duration);
}

const SALTO = 5;

function alTeclear(evento) {
  if (!props.buscable || !props.duration) return;
  const saltos = {
    ArrowRight: SALTO,
    ArrowUp: SALTO,
    ArrowLeft: -SALTO,
    ArrowDown: -SALTO,
    Home: -Infinity,
    End: Infinity,
  };
  if (!(evento.key in saltos)) return;
  evento.preventDefault();
  const destino = Math.min(Math.max(props.elapsed + saltos[evento.key], 0), props.duration);
  emit("buscar", destino);
}

function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const time = computed(() => {
  if (props.loading) return "—— / ——";
  if (!props.duration) return clock(props.elapsed);
  return `${clock(props.elapsed)} / ${clock(props.duration)}`;
});
</script>

<template>
  <div
    class="transport"
    :class="{
      'is-loading': loading,
      'is-idle': !playing && !loading,
      'transport--sobre-imagen': sobreImagen,
    }"
    :aria-busy="loading ? 'true' : null"
  >
    <div class="transport__wave">
      <div v-if="analisis !== 'no'" class="transport__meter" aria-hidden="true">
        <span
          v-for="(bar, i) in bars"
          :key="i"
          class="transport__bar"
          :style="{ height: `${bar.h}px`, transform: `scaleY(${bar.scale})` }"
        />
      </div>

      <!-- Sin duración conocida no hay progreso que mostrar, y un raíl vacío
           que nunca se llena se lee como «0% reproducido», no como directo.

           ⚠️ Con `buscable` deja de ser un indicador y pasa a ser un mando, así
           que cambia de etiqueta: `slider` en vez de `progressbar`, y con
           teclado, que una barra que solo responde al ratón deja fuera a quien
           navega tabulando. Las flechas mueven cinco segundos, que es el salto
           con el que se busca un estribillo. -->
      <div
        v-if="duration || loading"
        class="transport__progress"
        :class="{ 'is-buscable': buscable && duration }"
        :role="buscable && duration ? 'slider' : 'progressbar'"
        :tabindex="buscable && duration ? 0 : null"
        :aria-label="buscable && duration ? 'Punto de la pista' : null"
        :aria-valuenow="Math.round(progress)"
        aria-valuemin="0"
        aria-valuemax="100"
        @pointerdown="alPinchar"
        @keydown="alTeclear"
      >
        <span class="transport__elapsed" :style="{ width: `${progress}%` }" />
        <span class="transport__playhead" :style="{ left: `${progress}%` }" />
      </div>
    </div>

    <div class="transport__time">{{ time }}</div>

    <!-- El control es el botón del sistema en su forma de icono. La celda —su
         ancho, su filete y, sobre imagen, el rojo macizo— la pone el módulo.


         🔴 **Glifo de 16**, del nodo `Pattern/NowPlaying 638:371 › transport
         2167:9813`: «control cuadrado de 65 con el glifo de 16 en
         `text/on-action`». Estuvo en 11 hasta el 21.09.26, que es cuando lo vio
         Pablo: en un cuadrado de 65 un glifo de 11 se pierde. -->
    <ButtonDs
      class="transport__ctl"
      variant="ghost"
      :icono="playing ? 'pause' : 'play'"
      :icono-tam="16"
      :etiqueta="playing ? 'Pausar emisión' : 'Reproducir emisión'"
      :disabled="disabled || loading"
      @click="$emit('toggle')"
    />
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.transport {
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  height: 65px;
  // Ancho completo con tope. Sin el 100% un contenedor flexible lo encoge al
  // contenido y la barra sale a 203px en vez de a los 540 del artefacto.
  width: 100%;
  max-width: 540px;
  border: 1px solid var(--color-border-default);
  border-radius: 0;

  // El módulo se apoya sobre la placa de señal, y con el lienzo opaco se
  // recortaba como un rectángulo pegado encima: el fondo se cortaba en seco en
  // sus bordes. Translúcido y con el fondo desenfocado detrás, la señal sigue
  // leyéndose a través y el módulo deja de estar «encima» para estar «sobre».
  //
  // No es una sombra ni un borde blando: la caja sigue siendo una línea de 1px
  // y una esquina recta. Lo único que cambia es que el fondo se ve.
  //
  // 25% de lienzo (Pablo, 26.08.26): el módulo casi no tiñe, y lo que separa la
  // onda de la placa es el desenfoque, no una capa de color.
  background: color-mix(in srgb, var(--color-bg-canvas) 25%, transparent);
  backdrop-filter: blur(14px);

  // Sin soporte de `backdrop-filter` el fondo translúcido dejaría la onda
  // ilegible sobre la imagen. Ahí vuelve el lienzo opaco, que es lo que había.
  @supports not (backdrop-filter: blur(1px)) {
    background: var(--color-bg-canvas);
  }
  overflow: hidden;
}

// ── Sobre imagen ──
//
// Entrega del 18.09.26 § 3. El módulo sigue siendo **una sola pieza**: el fondo
// y el filete van corridos de lado a lado, y los ocho de aire antes del control
// son superficie suya, no un hueco por el que asome la foto.
//
// ⚠️ **El filete no entra en el reparto**: es una línea encima, no una pieza que
// ocupe. Por eso va en `outline` hacia dentro y no en `border`: con `border`,
// los dos píxeles se comen el ancho y el reparto 282+123+8+65 deja de cuadrar.
//
// Los tres tokens de tinta **no se invierten**, así que esto se ve igual en
// claro y en oscuro, que es justo lo que se quiere sobre una foto.
.transport--sobre-imagen {
  border: 0;
  // 🔴 **Sin desenfoque y con el filete apenas perceptible.** Encargo de Pablo
  // (18.09.26), comparando lo montado con el artefacto: ahí la foto se ve
  // **nítida** a través del módulo, que es una lámina de vidrio y no un cristal
  // esmerilado. El `backdrop-filter` que traía el módulo desde el 26.08.26
  // convertía el fondo en una mancha, y eso es lo que pesaba.
  //
  // El filete marca el canto y nada más: 12 % de tinta clara, igual en los dos
  // temas. Con la línea maciza el módulo recortaba un rectángulo contra la foto.
  backdrop-filter: none;
  outline: 1px solid color-mix(in srgb, var(--color-text-on-ink) 12%, transparent);
  outline-offset: -1px;
  // 🔴 **Cuánta tinta lo decide quien lo monta**, porque depende de lo que haya
  // debajo. En la portada el módulo se apoya en la meseta del velo, y el 25 %
  // de la entrega basta. En Radio no hay velo: debajo está la placa de señal,
  // que en claro es pálida, y ahí `NowPlaying` la baja al 12 % para que el
  // módulo se lea como vidrio y no como una plancha gris.
  background: color-mix(
    in srgb,
    var(--color-bg-ink) var(--transport-tinta, 25%),
    transparent
  );

  // Reloj fijo, que es lo que hace que el reparto sea una regla y no un número.
  //
  // 🔴 **La tinta del reloj la decide lo que hay debajo, no el tema.** Por
  // defecto `text/on-ink`, que es lo que pide la entrega y lo que vale sobre la
  // meseta del hero y sobre la placa en oscuro. Donde debajo hay una superficie
  // clara —la placa de Radio en tema claro— quien monta el módulo la cambia:
  // con el reloj blanco harían falta 62 % de tinta para llegar a AA, y eso
  // convierte la barra en una caja opaca. Medido: blanco sobre placa clara da
  // 2,1:1; oscuro, 7,2:1 con la misma tinta del 25 %.
  .transport__time {
    flex: none;
    width: 123px;
    padding: 0;
    border-left-color: color-mix(in srgb, var(--color-text-on-ink) 12%, transparent);
    color: var(--transport-tinta-texto, var(--color-text-on-ink));
  }

  // Aire, no separación: ocho píxeles y **ninguna línea en medio**. Poner las
  // dos cosas es ruido.
  .transport__ctl {
    width: 65px;
    margin-left: 8px;
    border-left: 0;
    // 🔴 **`action/primary`, no `bg/accent`** (diseño, 21.09.26): el play es una
    // acción y tiene hover y pulsado; `bg/accent` es para superficies rojas que
    // no se pulsan. Hoy valen lo mismo —los dos son `red/600`— y la igualdad es
    // deliberada, pero el token dice qué es la pieza.
    background: var(--color-action-primary-default);
    color: var(--color-text-on-action);

    &:hover:not(:disabled) {
      background: var(--color-action-primary-hover);
      // El glifo se queda en papel: sobre el rojo, el rojo del hover general no
      // se vería.
      color: var(--color-text-on-action);
    }
  }
}

// ── Onda y progreso ──
// `width: 0` además de `flex: 1 1 0`: con hijos en `nowrap`, min-width no
// basta y Chrome sigue dimensionando por el min-content. Es la causa de casi
// todos los desbordamientos horizontales del proyecto.
.transport__wave {
  display: flex;
  flex: 1 1 0;
  width: 0;
  min-width: 0;
  flex-direction: column;
  // El raíl se centra cuando se queda solo: sin medidor, un progreso pegado
  // arriba deja el bloque descompensado contra el reloj y el botón, que sí
  // están centrados.
  justify-content: center;
  gap: 9px;
  padding: 12px 14px;
  overflow: hidden;
}

.transport__meter {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 28px;
  overflow: hidden;
}

// Sin raíl de progreso la onda se queda sola en la celda y tiene que
// centrarse, o cuelga del borde superior.
.transport__wave:only-child,
.transport__meter:only-child {
  margin: auto 0;
}

// 🔴 La masa neutra va en `text/tertiary`, NO en `border/strong` como dice el
// nodo. El nodo no está mal: está dibujado en CLARO, y ahí `border/strong` es
// casi negro sobre lino. El problema es que ese token **no es simétrico entre
// temas** —se invierte a un gris oscuro— y en oscuro desaparece:
//
//     border/strong    claro 13,83:1     oscuro **1,77:1**   ← invisible
//     text/tertiary    claro  3,62:1     oscuro   2,75:1
//     action/primary   claro  5,05:1     oscuro   4,46:1     ← las rojas
//
// Reportado por Pablo el 26.08: «las ondas grises son casi imperceptibles en
// producción». Y tiene sentido — un token de BORDE está pensado para líneas de
// 1px, donde ese contraste basta porque el ojo las lee como separación. La onda
// no es una separación: es contenido, y hay que verla.
//
// `tertiary` y no `secondary` (6,67:1 en oscuro) porque el rojo tiene que
// seguir mandando: con `secondary` la masa neutra pesaría MÁS que el acento y
// se invertiría la jerarquía que dice el patrón —«la masa es neutra y el rojo
// la mide»—.
.transport__bar {
  flex: none;
  width: 3px;
  background: var(--color-text-tertiary);
  transform-origin: bottom;
  transition: transform 0.08s linear;

  // Una de cada tres en rojo. La masa es neutra y el rojo la mide.
  &:nth-child(3n) {
    background: var(--color-action-primary-default);
  }
}

.transport__progress {
  position: relative;
  height: 2px;
  background: var(--color-border-default);
}

// Con el mando encendido, la zona sensible sube a 14 sin engordar la línea: dos
// píxeles son un objetivo imposible con el ratón y aún más con el dedo. El raíl
// sigue midiendo 2 y el relleno se pinta sobre él.
.transport__progress.is-buscable {
  height: 14px;
  margin: -6px 0;
  padding: 6px 0;
  background: none;
  cursor: pointer;
  touch-action: none;

  &::before {
    content: "";
    position: absolute;
    right: 0;
    left: 0;
    top: 6px;
    height: 2px;
    background: var(--color-border-default);
  }

  &:focus-visible {
    outline: 1px solid var(--color-border-focus);
    outline-offset: 2px;
  }

  .transport__elapsed,
  .transport__playhead {
    top: 6px;
    bottom: auto;
    height: 2px;
  }
}

.transport__elapsed {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: var(--color-action-primary-default);
}

// Aguja de 1px y 8px de alto, centrada en la regla.
.transport__playhead {
  position: absolute;
  top: 50%;
  width: 1px;
  height: 8px;
  transform: translateY(-50%);
  background: var(--color-action-primary-default);
}

// ── Tiempo ──
.transport__time {
  @include t.mono-label;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-left: 1px solid var(--color-border-default);
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

// ── Control ──
// Celda del módulo: ocupa todo el alto y se separa con línea, no con espacio.
// Del botón solo se ajusta lo que es del módulo.
.transport__ctl {
  flex: none;
  width: 48px;
  height: auto;
  align-self: stretch;
  border: 0;
  border-left: 1px solid var(--color-border-default);
  transition: background 0.12s linear, color 0.12s linear;

  // 🔴 **Macizo siempre, también parado.** Entrega de diseño del 21.09.26: el
  // botón de reproducción **no cambia de forma entre parado y sonando, a ningún
  // ancho**; lo que cambia es el glifo. Hasta hoy este se rellenaba solo al
  // pasar el ratón, y era la única forma del play distinta a las demás —la
  // cabecera y el arte ya iban macizos—.
  background: var(--color-action-primary-default);
  color: var(--color-text-on-action);

  // El hover solo oscurece el rojo.
  &:hover:not(:disabled) {
    // 🔴 **El glifo se queda en blanco.** El hover del `ghost` lo pasa al
    // acento, y sobre el rojo no se veía (Pablo, 01.10.26). Lleva `:not(:disabled)`
    // para pesar lo mismo que la regla de `ButtonDs` y ganarle.
    background: var(--color-action-primary-hover);
    color: var(--color-text-on-action);
  }

  &:active:not(:disabled) {
    background: var(--color-action-primary-pressed);
    color: var(--color-text-on-action);
  }

  // ⚠️ Sin pista que reproducir **no se pinta rojo**, que sería prometer una
  // acción que no hay. Es el único estado en el que el botón no es macizo, y no
  // está en la entrega: la tabla de diseño cubre reposo, hover, pulsado y foco.
  &:disabled {
    background: transparent;
    color: var(--color-icon-disabled);
  }
}

// ── Parado ──
// Además de bajar, la masa se apaga: en reposo el rojo tampoco significa nada,
// y una de cada tres barras en acento sobre una onda quieta seguiría diciendo
// «esto está vivo».
// En reposo TODAS pierden el rojo y bajan un escalón: no suena nada, así que la
// onda está pero no habla. `border/strong` y no `border/default`, que en oscuro
// es 1,31:1 y no se ve en absoluto — la forma de reposo tiene que leerse como
// forma, no como un hueco vacío.
.transport.is-idle .transport__bar {
  background: var(--color-border-strong);
  transition: transform 0.2s ease, background 0.2s linear;
}

// ── Cargando ──
.transport.is-loading {
  .transport__bar {
    background: var(--color-border-default);
    transform: scaleY(0.3);
  }

  .transport__elapsed {
    width: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .transport__bar {
    transition: none;
  }
}
</style>
